import React, { useEffect, useMemo, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { KeyRound, Pause, Play, Printer, ShieldCheck, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

/**
 * AdminCPCohort — CP-1.2 (Jun 2026) · Controlled Production cohort.
 *
 * Admin-only console to provision controlled-production users
 * (email + server-generated temporary password shown ONCE), assign
 * profile/role/scope, link operators, reset temp passwords and
 * suspend/reactivate — all backed by `/admin/controlled-production/*`.
 *
 * The "welcome kit" dialog renders the one-time credentials in a
 * print-isolated card for MANUAL delivery by José/Lumia (hard block:
 * never emailed).
 */
const PROFILES = [
  "admin", "staff", "qa_manager", "qa_tester",
  "shop_manager", "operator", "racket_technician",
];
const SHOP_SCOPED = new Set(["shop_manager", "operator", "racket_technician"]);

const STR = {
  es: {
    title: "Cohorte de producción controlada",
    subtitle: "Usuarios provisionados por TEAM · sin registro público · credenciales de entrega manual · sin envíos reales",
    provision: "Provisionar usuario",
    email: "Email", fullName: "Nombre (opcional)", profile: "Perfil",
    shops: "Tiendas (scope)", operator: "Operador vinculado (opcional)",
    none: "— ninguno —", submit: "Provisionar", cancel: "Cancelar",
    kitTitle: "Kit de bienvenida · MOSTRADO UNA SOLA VEZ",
    kitReusedTitle: "Identidad reutilizada (sin nueva contraseña)",
    kitUrl: "URL de acceso", kitEmail: "Email", kitTemp: "Contraseña temporal",
    kitProfile: "Perfil", kitRole: "Rol", kitScope: "Tiendas",
    kitInstructions: "Primer login: el sistema OBLIGA a cambiar la contraseña antes de continuar.",
    kitDelivery: "Entrega manual por José/Lumia o admin autorizado. NO enviar por email.",
    kitPrint: "Imprimir kit", kitClose: "Cerrar (no se volverá a mostrar)",
    tblUser: "Usuario", tblRole: "Rol / RL", tblScope: "Scope", tblState: "Estado", tblActions: "Acciones",
    tblActivity: "Actividad", lastLogin: "Último acceso", lastLoginNone: "nunca",
    mustChange: "Cambio de contraseña pendiente", pwOk: "Contraseña cambiada",
    suspended: "Suspendido", active: "Activo",
    resetTemp: "Reset contraseña temporal", suspend: "Suspender", reactivate: "Reactivar",
    confirmReset: "¿Generar nueva contraseña temporal? La actual dejará de funcionar.",
    confirmSuspend: "¿Suspender este usuario? No podrá iniciar sesión (reversible).",
    empty: "Aún no hay usuarios provisionados.",
    provisionedOk: "Usuario provisionado", reusedOk: "Identidad existente reutilizada",
    loading: "Cargando cohorte…",
  },
  en: {
    title: "Controlled production cohort",
    subtitle: "TEAM-provisioned users · no public registration · manual credential delivery · no real sends",
    provision: "Provision user",
    email: "Email", fullName: "Name (optional)", profile: "Profile",
    shops: "Shops (scope)", operator: "Linked operator (optional)",
    none: "— none —", submit: "Provision", cancel: "Cancel",
    kitTitle: "Welcome kit · SHOWN ONLY ONCE",
    kitReusedTitle: "Identity reused (no new password)",
    kitUrl: "Access URL", kitEmail: "Email", kitTemp: "Temporary password",
    kitProfile: "Profile", kitRole: "Role", kitScope: "Shops",
    kitInstructions: "First login: the system FORCES a password change before continuing.",
    kitDelivery: "Manual delivery by José/Lumia or an authorised admin · NO email · NO WhatsApp.",
    kitChecklistTitle: "Basic test checklist (QA cohort)",
    kitChecklist: [
      "Log in with the temporary password and complete the forced change.",
      "Verify you only see the sections allowed for your profile.",
      "Create a QA ticket (flagged as test, never operational).",
      "Walk the status transitions and upload one test photo.",
      "Confirm the blocks: no real sends, no QR, no foreign sections.",
    ],
    kitPrint: "Print sheet", kitClose: "Close (will not be shown again)",
    tblUser: "User", tblRole: "Role / RL", tblScope: "Scope", tblState: "State", tblActions: "Actions",
    tblActivity: "Activity", lastLogin: "Last login", lastLoginNone: "never",
    mustChange: "Password change pending", pwOk: "Password changed",
    suspended: "Suspended", active: "Active",
    resetTemp: "Reset temp password", suspend: "Suspend", reactivate: "Reactivate",
    confirmReset: "Generate a new temporary password? The current one will stop working.",
    confirmSuspend: "Suspend this user? They will not be able to log in (reversible).",
    empty: "No provisioned users yet.",
    provisionedOk: "User provisioned", reusedOk: "Existing identity reused",
    loading: "Loading cohort…",
  },
};

const AdminCPCohort = () => {
  const { lang } = useLang();
  const s = STR[lang === "es" ? "es" : "en"];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [kit, setKit] = useState(null);
  const [shops, setShops] = useState([]);
  const [operators, setOperators] = useState([]);
  const [form, setForm] = useState({
    email: "", full_name: "", profile: "qa_tester", shop_ids: [], operator_id: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/controlled-production/cohort");
      setItems(data.items || []);
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/admin/racket-lab/shops").then((r) => setShops(r.data?.items || [])).catch(() => {});
    api.get("/admin/racket-lab/operators").then((r) => setOperators(r.data?.items || [])).catch(() => {});
  }, []);

  const needsShops = SHOP_SCOPED.has(form.profile);
  const orgFromShops = useMemo(() => {
    const first = shops.find((sh) => form.shop_ids.includes(sh.id));
    return first?.organisation_id || null;
  }, [shops, form.shop_ids]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const body = {
        email: form.email.trim(),
        full_name: form.full_name.trim() || null,
        profile: form.profile,
        shop_ids: needsShops ? form.shop_ids : [],
        organisation_id: needsShops ? orgFromShops : null,
        operator_id: form.operator_id || null,
      };
      const { data } = await api.post("/admin/controlled-production/provision", body);
      toast.success(data.created ? s.provisionedOk : s.reusedOk);
      setDialogOpen(false);
      setForm({ email: "", full_name: "", profile: "qa_tester", shop_ids: [], operator_id: "" });
      setKit({ ...data.welcome_kit, created: data.created });
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const resetTemp = async (uid) => {
    if (!window.confirm(s.confirmReset)) return;
    try {
      const { data } = await api.post(`/admin/controlled-production/users/${uid}/reset-temp-password`);
      const u = items.find((x) => x.id === uid);
      setKit({
        access_url: window.location.origin, email: u?.email,
        temp_password: data.temp_password, profile: u?.rl_promotion?.role,
        jwt_role: u?.role, rl_role: u?.rl_promotion?.role,
        shop_ids: u?.rl_promotion?.shop_ids || [], created: true,
      });
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const toggleSuspend = async (u) => {
    const action = u.is_active === false ? "reactivate" : "suspend";
    if (action === "suspend" && !window.confirm(s.confirmSuspend)) return;
    try {
      await api.post(`/admin/controlled-production/users/${u.id}/${action}`);
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const toggleShop = (id) =>
    setForm((f) => ({
      ...f,
      shop_ids: f.shop_ids.includes(id) ? f.shop_ids.filter((x) => x !== id) : [...f.shop_ids, id],
    }));

  return (
    <div data-testid="cp-cohort-page" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> {s.title}
          </h1>
          <p className="text-xs text-slate-500">{s.subtitle}</p>
        </div>
        <button
          type="button"
          data-testid="cp-provision-btn"
          onClick={() => setDialogOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
        >
          <UserPlus className="h-3.5 w-3.5" /> {s.provision}
        </button>
      </div>

      {/* Cohort table */}
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        {loading ? (
          <p className="p-4 text-xs text-slate-500">{s.loading}</p>
        ) : items.length === 0 ? (
          <p data-testid="cp-cohort-empty" className="p-4 text-xs text-slate-500">{s.empty}</p>
        ) : (
          <table data-testid="cp-cohort-table" className="w-full text-xs">
            <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">{s.tblUser}</th>
                <th className="px-3 py-2">{s.tblRole}</th>
                <th className="px-3 py-2">{s.tblScope}</th>
                <th className="px-3 py-2">{s.tblState}</th>
                <th className="px-3 py-2">{s.tblActivity}</th>
                <th className="px-3 py-2">{s.tblActions}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} data-testid={`cp-cohort-row-${u.id}`} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <p className="font-medium text-slate-800">{u.email}</p>
                    <p className="text-[10px] text-slate-400">{u.full_name}</p>
                  </td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5">{u.role}</span>
                    {u.rl_promotion?.role ? (
                      <span className="ml-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-indigo-700">RL: {u.rl_promotion.role}</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-slate-500">
                    {(u.rl_promotion?.shop_ids || []).length
                      ? u.rl_promotion.shop_ids.map((id) => shops.find((sh) => sh.id === id)?.name || id.slice(0, 8)).join(", ")
                      : "global"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${u.is_active === false ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {u.is_active === false ? s.suspended : s.active}
                    </span>
                    <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[9px] ${u.must_change_password ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                      {u.must_change_password ? s.mustChange : s.pwOk}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[10px] text-slate-500" data-testid={`cp-cohort-activity-${u.id}`}>
                    <p>{s.lastLogin}: {u.last_login_at ? u.last_login_at.slice(0, 16).replace("T", " ") : s.lastLoginNone}</p>
                    {u.qa_tickets_count > 0 ? (
                      <span className="mt-0.5 inline-block rounded-full bg-purple-100 px-1.5 py-0.5 text-[9px] font-medium text-purple-700">
                        QA: {u.qa_tickets_count}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button type="button" data-testid={`cp-reset-temp-${u.id}`} title={s.resetTemp}
                        onClick={() => resetTemp(u.id)}
                        className="rounded p-1 text-slate-500 hover:bg-slate-100">
                        <KeyRound className="h-3.5 w-3.5" />
                      </button>
                      <button type="button"
                        data-testid={`cp-${u.is_active === false ? "reactivate" : "suspend"}-${u.id}`}
                        title={u.is_active === false ? s.reactivate : s.suspend}
                        onClick={() => toggleSuspend(u)}
                        className="rounded p-1 text-slate-500 hover:bg-slate-100">
                        {u.is_active === false ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Provision dialog */}
      {dialogOpen ? (
        <div data-testid="cp-provision-dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <form onSubmit={submit} className="w-full max-w-md space-y-3 rounded-lg border border-slate-200 bg-white p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">{s.provision}</h2>
              <button type="button" onClick={() => setDialogOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            </div>
            <input data-testid="cp-provision-email-input" type="email" required value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder={s.email} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
            <input data-testid="cp-provision-fullname-input" type="text" value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              placeholder={s.fullName} maxLength={120} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
            <select data-testid="cp-provision-profile-select" value={form.profile}
              onChange={(e) => setForm((f) => ({ ...f, profile: e.target.value, shop_ids: [] }))}
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm">
              {PROFILES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {needsShops ? (
              <div data-testid="cp-provision-shop-select" className="space-y-1 rounded-md border border-slate-200 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">{s.shops}</p>
                <div className="flex flex-wrap gap-1">
                  {shops.map((sh) => (
                    <button key={sh.id} type="button" onClick={() => toggleShop(sh.id)}
                      className={`rounded-full px-2 py-0.5 text-[10px] ${form.shop_ids.includes(sh.id) ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                      {sh.name || sh.id.slice(0, 8)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <select data-testid="cp-provision-operator-select" value={form.operator_id}
              onChange={(e) => setForm((f) => ({ ...f, operator_id: e.target.value }))}
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm">
              <option value="">{s.operator}: {s.none}</option>
              {operators.map((o) => <option key={o.id} value={o.id}>{o.display_label || o.id}</option>)}
            </select>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setDialogOpen(false)}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700">{s.cancel}</button>
              <button type="submit" data-testid="cp-provision-submit" disabled={busy || (needsShops && form.shop_ids.length === 0)}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60">
                {s.submit}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {/* Welcome kit (one-time) */}
      {kit ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <style>{`@media print {
            body * { visibility: hidden; }
            #cp-welcome-kit-card, #cp-welcome-kit-card * { visibility: visible; }
            #cp-welcome-kit-card { position: absolute; left: 0; top: 0; box-shadow: none !important; }
          }`}</style>
          <div className="w-full max-w-md space-y-3">
            <div id="cp-welcome-kit-card" data-testid="cp-welcome-kit"
              className="rounded-lg border-2 border-emerald-600 bg-white p-5 shadow-xl">
              <h2 className="mb-2 text-sm font-bold text-emerald-700">
                {kit.created && kit.temp_password ? s.kitTitle : s.kitReusedTitle}
              </h2>
              <dl className="space-y-1.5 text-xs">
                <div className="flex justify-between gap-2"><dt className="text-slate-500">{s.kitUrl}</dt><dd className="font-mono text-[10px]">{kit.access_url || window.location.origin}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">{s.kitEmail}</dt><dd className="font-medium">{kit.email}</dd></div>
                {kit.temp_password ? (
                  <div className="flex justify-between gap-2 rounded bg-amber-50 px-1.5 py-1">
                    <dt className="text-amber-700">{s.kitTemp}</dt>
                    <dd data-testid="cp-kit-temp-password" className="font-mono font-bold">{kit.temp_password}</dd>
                  </div>
                ) : null}
                <div className="flex justify-between gap-2"><dt className="text-slate-500">{s.kitProfile}</dt><dd>{kit.profile}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">{s.kitRole}</dt><dd>{kit.jwt_role} {kit.rl_role ? `· RL: ${kit.rl_role}` : ""}</dd></div>
                {(kit.shop_ids || []).length ? (
                  <div className="flex justify-between gap-2"><dt className="text-slate-500">{s.kitScope}</dt><dd className="text-[10px]">{kit.shop_ids.join(", ")}</dd></div>
                ) : null}
              </dl>
              <p className="mt-3 rounded-md bg-slate-50 p-2 text-[10px] text-slate-600">{s.kitInstructions}</p>
              {(kit.jwt_role === "qa_tester" || kit.jwt_role === "qa_manager" || kit.profile === "qa_tester" || kit.profile === "qa_manager") && (
                <div data-testid="cp-kit-qa-checklist" className="mt-2 rounded-md border border-indigo-100 bg-indigo-50/60 p-2">
                  <p className="text-[10px] font-semibold text-indigo-800">{s.kitChecklistTitle}</p>
                  <ol className="mt-1 list-decimal space-y-0.5 pl-4 text-[10px] text-indigo-900">
                    {s.kitChecklist.map((item) => (<li key={item}>{item}</li>))}
                  </ol>
                </div>
              )}
              <p className="mt-1 text-[9px] text-rose-600">{s.kitDelivery}</p>
            </div>
            <div className="flex justify-end gap-2 print:hidden">
              <button type="button" data-testid="cp-kit-print-btn" onClick={() => window.print()}
                className="inline-flex items-center gap-1 rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white">
                <Printer className="h-3.5 w-3.5" /> {s.kitPrint}
              </button>
              <button type="button" data-testid="cp-kit-close" onClick={() => setKit(null)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700">
                {s.kitClose}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminCPCohort;
